"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import {
    calculateChecklistBlankLineCounts,
    getPrintablePageHeightPx,
    MIN_BLANK_LINE_COUNT,
} from "../utils/tools-supplies-blank-lines"

const DEFAULT_ROW_STEP_PX = 36

const PRINT_MEASURE_STYLE_ID = "work-order-print-measure-styles"

const PRINT_MEASURE_CSS = `
  .print-measure-mode .print\\:hidden {
    display: none !important;
  }
  .print-measure-mode .hidden.print\\:block {
    display: block !important;
  }
  .print-measure-mode .hidden.print\\:flex {
    display: flex !important;
  }
  .print-measure-mode .hidden.print\\:grid {
    display: grid !important;
  }
`

function ensurePrintMeasureStyles() {
    if (document.getElementById(PRINT_MEASURE_STYLE_ID)) return
    const style = document.createElement("style")
    style.id = PRINT_MEASURE_STYLE_ID
    style.textContent = PRINT_MEASURE_CSS
    document.head.appendChild(style)
}

function measureRowStepPx(
    rowHeightMeasureRef: React.RefObject<HTMLDivElement | null>
): number {
    const rowHeight = rowHeightMeasureRef.current?.getBoundingClientRect().height
    if (!rowHeight || rowHeight <= 0) {
        return DEFAULT_ROW_STEP_PX
    }
    return rowHeight + 8
}

export function usePrintChecklistBlankLines(
    toolsItemCount: number,
    suppliesItemCount: number
) {
    const checklistSectionRef = useRef<HTMLDivElement>(null)
    const toolsColumnRef = useRef<HTMLDivElement>(null)
    const suppliesColumnRef = useRef<HTMLDivElement>(null)
    const toolsItemsRef = useRef<HTMLDivElement>(null)
    const suppliesItemsRef = useRef<HTMLDivElement>(null)
    const rowHeightMeasureRef = useRef<HTMLDivElement>(null)

    const [printBlankLines, setPrintBlankLines] = useState({
        tools: 0,
        supplies: 0,
    })

    const calculateBlankLines = useCallback(() => {
        ensurePrintMeasureStyles()
        document.documentElement.classList.add("print-measure-mode")
        void document.body.offsetHeight

        try {
            const toolsItems = toolsItemsRef.current
            const suppliesItems = suppliesItemsRef.current

            if (!toolsItems || !suppliesItems) {
                return {
                    tools: MIN_BLANK_LINE_COUNT,
                    supplies: MIN_BLANK_LINE_COUNT,
                }
            }

            return calculateChecklistBlankLineCounts({
                pageHeightPx: getPrintablePageHeightPx(),
                toolsItemsBottomPx: toolsItems.getBoundingClientRect().bottom,
                suppliesItemsBottomPx: suppliesItems.getBoundingClientRect().bottom,
                toolsItemCount,
                suppliesItemCount,
                rowStepPx: measureRowStepPx(rowHeightMeasureRef),
            })
        } finally {
            document.documentElement.classList.remove("print-measure-mode")
        }
    }, [toolsItemCount, suppliesItemCount])

    const prepareForPrint = useCallback(() => {
        window.scrollTo(0, 0)

        flushSync(() => {
            setPrintBlankLines({ tools: 0, supplies: 0 })
        })

        void document.body.offsetHeight

        const counts = calculateBlankLines()
        flushSync(() => {
            setPrintBlankLines(counts)
        })
    }, [calculateBlankLines])

    const resetAfterPrint = useCallback(() => {
        setPrintBlankLines({ tools: 0, supplies: 0 })
    }, [])

    useEffect(() => {
        const onBeforePrint = () => prepareForPrint()
        const onAfterPrint = () => resetAfterPrint()

        window.addEventListener("beforeprint", onBeforePrint)
        window.addEventListener("afterprint", onAfterPrint)

        return () => {
            window.removeEventListener("beforeprint", onBeforePrint)
            window.removeEventListener("afterprint", onAfterPrint)
        }
    }, [prepareForPrint, resetAfterPrint])

    return {
        checklistSectionRef,
        toolsColumnRef,
        suppliesColumnRef,
        toolsItemsRef,
        suppliesItemsRef,
        rowHeightMeasureRef,
        printBlankLines,
        prepareForPrint,
        resetAfterPrint,
    }
}
